import {
  Button,
  Checkbox,
  Grid,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axios';
import Logo from '../Logo/Logo';
import { EquipmentTypeInner } from '../../utils/types';

type NewEquipmentProps = {
  isOpen: boolean;
  onClose: () => void;
  playgroundId?: string;
  selectedEquipment: number[];
};

const NewEquipment = ({
  isOpen,
  onClose,
  playgroundId,
  selectedEquipment,
}: NewEquipmentProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [checked, setChecked] = useState<number[]>(selectedEquipment);
  const queryClient = useQueryClient();

  const equipmentsQuery = useQuery({
    queryKey: ['equipments'],
    queryFn: () =>
      axios({ url: '/equipments/available' }).then((response) => response.data),
  });

  const { data: equipmentsData } = equipmentsQuery;

  const handleEquipmentChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const equipmentId = +event.target.value;
      const updatedSelected = checked.includes(equipmentId)
        ? checked.filter((item) => item !== equipmentId)
        : [...checked, equipmentId];
      setChecked(updatedSelected);
    },
    [checked],
  );

  const addEquipmentMutation = useMutation(
    async (data: { playgroundId: string; equipmentIds: number[] }) => {
      const response = await axios.put(
        `/playgrounds/${data.playgroundId}/equipments`,
        data,
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast({
          title: t('add_new_equipment_modal.success_add_equipment'),
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
        queryClient.invalidateQueries(['playgrounds']);
        onClose();
      },
      onError: (error: any) => {
        toast({
          title: t('add_new_equipment_modal.errors.failed_add_equipment'),
          description: error.message,
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
      },
    },
  );

  const onAdd = useCallback(() => {
    if (!playgroundId) {
      return;
    }

    addEquipmentMutation.mutate({
      playgroundId,
      equipmentIds: checked,
    });
  }, [addEquipmentMutation, checked, playgroundId]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      autoFocus
      isCentered
      id="add_equipments_modal"
    >
      <form>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Logo />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Heading size="md" mb={5}>
              {t('add_new_equipment_modal.title')}
            </Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              {equipmentsData &&
                equipmentsData.map((equipment: EquipmentTypeInner) => (
                  <Checkbox
                    key={equipment.id}
                    value={equipment.id}
                    onChange={handleEquipmentChange}
                    defaultChecked={checked.includes(+equipment.id)}
                    readOnly={checked.includes(+equipment.id)}
                    disabled={checked.includes(+equipment.id)}
                    colorScheme="teal"
                  >
                    {t(
                      `equipments.${equipment.name.includes(' ') ? equipment.name.split(' ').join('_') : equipment.name}`,
                    )}
                  </Checkbox>
                ))}
            </Grid>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              onClick={onClose}
              colorScheme="teal"
              variant="outline"
              w="full"
              mt={3}
            >
              {t('button.cancel')}
            </Button>
            <Button
              onClick={onAdd}
              colorScheme="teal"
              variant="solid"
              w="full"
              mt={3}
            >
              {t('button.add')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

NewEquipment.defaultProps = {
  playgroundId: undefined,
};

export default NewEquipment;
